import { apiPaths, RefreshIntervalsEnum } from 'configs/apiConfigs'
import { getCallJobClaimData } from 'features/claims/claimsApi'
import moment from 'moment'
import useSWR from 'swr'
import { dotNetInteger32 } from 'utils'
import { encodeUri } from 'utils/api'
import { errorHandler } from 'utils/errors/errors'
import { getCall, getCallResource, getCalls } from './callsApi'
import { CallResourcesEnum, CallStatusEnum, CallTypeToJobTypeEnum } from './types/callsEnums'

export const useCall = (callId?: string | null, config?: any) => {
  const { data, error } = useSWR(callId ? apiPaths.call(callId) : null, getCall, { ...(config ? config : {}) })

  return {
    data,
    error,
    isLoading: !data && !error,
  }
}

export const useCurrentUserCalls = (id?: string, type?: 'live' | 'notDispositioned', config?: any) => {
  const path = {
    live: 'jobCompleted',
    notDispositioned: 'disposition',
  }[type || 'live']

  //TODO Fix the limit for completed IVR Calls, maybe add pagination?
  let query = id
    ? `?Limit=${dotNetInteger32}&Filter=${encodeUri({
        path: 'initiatorUserId',
        operator: '=',
        value: id,
        // path=jobCompleted returns all calls the have jobCompleted undefined, same for disposition
      })}&Filter=${encodeUri({ path })}`
    : ''

  if (!type || type === 'live') {
    query += `&Filter=${encodeUri({
      path: 'status',
      operator: 'in',
      valueList: [CallStatusEnum.InProgress, CallStatusEnum.Queued, CallStatusEnum.RetryPending],
    })}`
  }

  // Don't add the diagnostic calls to the query
  if (type === 'notDispositioned') {
    query += `&Filter=${encodeUri({
      path: 'jobType',
      operator: 'in',
      valueList: [
        CallTypeToJobTypeEnum.claimStatus,
        CallTypeToJobTypeEnum.attendable,
        CallTypeToJobTypeEnum.humanAgent,
        CallTypeToJobTypeEnum.standard,
        CallTypeToJobTypeEnum.APIIntegration,
      ],
    })}`
    query += `&Filter=${encodeUri({
      path: 'jobRequested',
      operator: '>=',
      value: moment.utc().subtract(7, 'd').format('YYYY-MM-DD'),
    })}`
  }

  const { data, error, mutate } = useSWR(id ? apiPaths.calls(query) : null, getCalls, {
    refreshInterval: RefreshIntervalsEnum.useCurrentUserCalls,
    onError: (err) => errorHandler.handleError(err, 'Could not fetch the current user calls.'),
    ...(config ? config : {}),
  })

  return {
    data,
    error,
    isLoading: !data && !error,
    mutate,
  }
}

export const useCallTranscript = (callId: string, config?: any) => {
  const { data, error } = useSWR(
    apiPaths.callResource(callId, CallResourcesEnum.transcripts, `?Limit=${dotNetInteger32}`),
    getCallResource,
    config || {},
  )

  return {
    data,
    error,
    isLoading: !data && !error,
  }
}

export const useCallJobClaimData = (jobId: string) => {
  const { data, error } = useSWR(apiPaths.callJobClaimData(jobId), getCallJobClaimData)

  return {
    data,
    error,
    isLoading: !data && !error,
  }
}
